import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { fetchSiloHistory, fetchSiloDeliveries, SiloDeliveryRow } from '../../store/Silo.store';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../configureStore';
import styles from './SiloHistory.module.scss';
import SiloIcon from '../SiloUebersicht/SiloIcon';
import Button from '../../shared/ui-components/Button/Button';
import settingsIcon from '../../assets/settings.svg';
import { routes } from '../../store/Workflow.store';
import SiloTooltip from './SiloTooltip';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import type { CellProps } from 'react-table';
import { OverviewTable } from '../../shared/ui-components/OverviewTable/OverviewTable';

const SiloHistory = (): JSX.Element => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { siloHistory, siloDeliveries, isLoading, error } = useSelector((state: RootState) => state.silo);
    const searchParams = new URLSearchParams(window.location.search);
    const siloId = searchParams.get('siloId');
    const siloTitle = useMemo(() => {
        const name = siloHistory?.siloName?.trim();
        if (!name) return '';
        return /^silo\b/i.test(name) ? name : `Silo ${name}`;
    }, [siloHistory?.siloName]);

    const NO_DATA_MESSAGE = 'Für den ausgewählten Zeitraum sind keine Messwerte vorhanden.';
    const TIME_RANGES = ['Heute', 'Letzte 7 Tage', 'Letzte 30 Tage'] as const;
    const Y_AXIS_OPTIONS = [
        { key: 'currentTons' as const, label: 'Tonnen' },
        { key: 'currentPercent' as const, label: 'Prozent' },
    ];

    useEffect(() => {
        if (siloId) {
            dispatch({ type: fetchSiloHistory.type, payload: siloId });
            dispatch({ type: fetchSiloDeliveries.type, payload: siloId });
        }
    }, [dispatch, siloId]);

    const [range, setRange] = useState<'Heute' | 'Letzte 7 Tage' | 'Letzte 30 Tage'>('Letzte 7 Tage');
    const [yAxisKey, setYAxisKey] = useState<'currentTons' | 'currentPercent'>('currentTons');

    const generateXAxisTicks = () => {
        const now = new Date();
        const ticks: number[] = [];

        if (range === 'Heute') {
            const nextFullHour = new Date(now.setMinutes(0, 0, 0)).getTime() + 60 * 60 * 1000;
            for (let i = nextFullHour - 24 * 60 * 60 * 1000; i <= nextFullHour; i += 2 * 60 * 60 * 1000) {
                ticks.push(i);
            }
        } else if (range === 'Letzte 7 Tage') {
            const nextFullDay = new Date(now.setHours(0, 0, 0, 0)).getTime() + 24 * 60 * 60 * 1000;
            for (let i = nextFullDay - 7 * 24 * 60 * 60 * 1000; i <= nextFullDay; i += 24 * 60 * 60 * 1000) {
                ticks.push(i);
            }
        } else if (range === 'Letzte 30 Tage') {
            const nextFullDay = new Date(now.setHours(0, 0, 0, 0)).getTime() + 24 * 60 * 60 * 1000;
            for (let i = nextFullDay - 30 * 24 * 60 * 60 * 1000; i <= nextFullDay; i += 5 * 24 * 60 * 60 * 1000) {
                ticks.push(i);
            }
        }

        return ticks;
    };

    const formatXAxis = (tick: number) => {
        const date = new Date(tick);
        if (range === 'Heute') {
            return `${String(date.getHours()).padStart(2, '0')}:00`;
        }
        return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
    };

    const xAxisTicks = useMemo(generateXAxisTicks, [range]);

    const getData = () => {
        if (!siloHistory?.fillLevels) return [];

        const now = new Date();
        const filterByRange = (days: number) => {
            const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
            return siloHistory.fillLevels.filter((entry) => new Date(entry.timestamp) >= cutoff);
        };

        let filteredData;
        if (range === 'Heute') {
            filteredData = filterByRange(1);
        } else if (range === 'Letzte 7 Tage') {
            filteredData = filterByRange(7);
        } else {
            filteredData = filterByRange(30);
        }

        // Add timestamp as a date for recharts to handle it correctly
        return filteredData.map((entry) => ({
            ...entry,
            timestamp: new Date(entry.timestamp).getTime(),
        }));
    };

    const filteredData = getData();

    const getLastUpdate = () => {
        if (!siloHistory?.fillLevels || siloHistory.fillLevels.length === 0) return 'Keine Daten verfügbar';
        const latestTimestamp = siloHistory.fillLevels
            .map((entry) => new Date(entry.timestamp))
            .sort((a, b) => b.getTime() - a.getTime())[0];
        return `Letzte Aktualisierung: ${latestTimestamp.toLocaleString()}`;
    };

    const getLatestPercent = (): number => {
        if (!siloHistory?.fillLevels || siloHistory.fillLevels.length === 0) return 0;
        const latest = siloHistory.fillLevels
            .filter((entry) => entry.currentPercent !== null)
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
        return latest?.currentPercent ?? 0;
    };

    type SiloDeliveryDisplay = (SiloDeliveryRow & { periodDisplay: string }) & Record<string, unknown>;

    const deliveriesData: SiloDeliveryDisplay[] = useMemo(() => {
        if (!siloDeliveries) return [];
        const formatDate = (d: string) => {
            const dt = new Date(d);
            const day = String(dt.getDate()).padStart(2, '0');
            const mon = String(dt.getMonth() + 1).padStart(2, '0');
            const yr = dt.getFullYear();
            return `${day}.${mon}.${yr}`;
        };
        return [...siloDeliveries]
            .sort((a, b) => new Date(b.periodEnd).getTime() - new Date(a.periodEnd).getTime())
            .slice(0, 5)
            .map((row) => ({
                ...row,
                periodDisplay: `${formatDate(row.periodStart)} - ${formatDate(row.periodEnd)}`,
            }));
    }, [siloDeliveries]);

    const deliveriesColumns: Column<SiloDeliveryDisplay>[] = useMemo(
        () => [
            {
                Header: 'Zeitraum',
                accessor: 'periodDisplay',
                sortType: (rowA, rowB) => new Date(rowA.original.periodEnd).getTime() - new Date(rowB.original.periodEnd).getTime(),
            },
            {
                Header: 'Artikel',
                accessor: 'articleName',
                Cell: ({ row }: CellProps<SiloDeliveryDisplay>) => (
                    <div>
                        <div>{row.original.articleName}</div>
                        <div className={styles.articleNumber}>{row.original.articleNumber}</div>
                    </div>
                ),
            },
            {
                Header: () => <div style={{ textAlign: 'right', width: '100%' }}>Anzahl Lieferungen</div>,
                accessor: 'deliveriesCount',
                Cell: ({ value }: CellProps<SiloDeliveryDisplay>) => (
                    <div style={{ textAlign: 'right', width: '100%' }}>{value as unknown as number}</div>
                ),
            },
        ],
        []
    );

    const deliveriesTableInstance = useTable<SiloDeliveryDisplay>(
        {
            columns: deliveriesColumns,
            data: deliveriesData,
            initialState: {
                sortBy: [
                    {
                        id: 'periodDisplay',
                        desc: true,
                    },
                ],
                pageSize: 5,
                pageIndex: 0,
            },
            disableMultiSort: true,
            disableSortRemove: true,
        },
        useSortBy,
        usePagination
    );

    const renderHeader = () => (
        <div className={styles.headerRow}>
            <h2 style={{ marginBottom: 0 }}>{siloTitle}</h2>
            <button
                className={styles.settingsButton}
                onClick={(): void => history.push(`${routes.siloEinstellungen}?siloId=${siloId}`)}
                aria-label="Einstellungen"
            >
                <img src={settingsIcon} alt="Einstellungen" style={{ width: 28, height: 28 }} />
                <span className={styles.settingsButtonText}>Einstellungen</span>
            </button>
        </div>
    );

    const renderButtonGroup = () => (
        <div className={styles.buttonGroup}>
            <div className={styles.innerButtonGroup}>
                {TIME_RANGES.map((r) => (
                    <Button key={r} onClick={() => setRange(r)} isSecondary={range !== r} isSmall additionalStyles={styles.centeredButton}>
                        <span className={styles.buttonText}>{r}</span>
                    </Button>
                ))}
            </div>

            <div className={styles.innerButtonGroup}>
                {Y_AXIS_OPTIONS.map((option) => (
                    <Button
                        key={option.key}
                        onClick={() => setYAxisKey(option.key)}
                        isSecondary={yAxisKey !== option.key}
                        isSmall
                        additionalStyles={styles.centeredButton}
                    >
                        <span className={styles.buttonText}>{option.label}</span>
                    </Button>
                ))}
            </div>
        </div>
    );

    if (error) {
        return <div>Fehler beim Laden der Silodaten: {error}</div>;
    }

    if (siloHistory?.fillLevels === null || siloHistory?.fillLevels.length === 0) {
        return (
            <LoadingSpinner isLoading={isLoading}>
                <div>
                    {renderHeader()}
                    {renderButtonGroup()}
                    <h3 className={styles.noEntriesAvailableMessage}>{NO_DATA_MESSAGE}</h3>
                </div>
            </LoadingSpinner>
        );
    }

    return (
        <LoadingSpinner isLoading={isLoading}>
            <div>
                {renderHeader()}
                {renderButtonGroup()}

                <p className={styles.lastUpdated}>{getLastUpdate()}</p>

                {filteredData.length === 0 ? (
                    <h3 className={styles.noEntriesAvailableMessage}>{NO_DATA_MESSAGE}</h3>
                ) : (
                    <div className={styles.contentRow}>
                        <div className={styles.siloIconWrapper}>
                            <SiloIcon percent={getLatestPercent()} width={200} height={233} />
                        </div>
                        <div className={styles.chartWrapper}>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="timestamp"
                                        type="number"
                                        domain={['dataMin', 'dataMax']}
                                        ticks={xAxisTicks}
                                        tickFormatter={formatXAxis}
                                        tick={{ dy: 7 }}
                                    />
                                    <YAxis
                                        dataKey={yAxisKey}
                                        domain={yAxisKey === 'currentPercent' ? [0, 100] : [0, 'auto']}
                                        tickFormatter={(value) => (yAxisKey === 'currentPercent' ? `${value} %` : `${value} t`)}
                                        tick={{ dx: -3 }}
                                    />
                                    <Tooltip content={<SiloTooltip />} isAnimationActive={false} />
                                    <Line
                                        type="monotone"
                                        dataKey={yAxisKey}
                                        stroke="#fd0"
                                        strokeWidth={2}
                                        dot={{ r: 3, stroke: '#fd0', fill: '#fd0' }}
                                        connectNulls={true}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {siloDeliveries && siloDeliveries.length > 0 && (
                    <div className={styles.deliveriesSection}>
                        <h3 className={styles.deliveriesTitle}>Vergangene Lieferungen</h3>
                        <OverviewTable tableInstance={deliveriesTableInstance} />
                        <div className={styles.backButtonContainer}>
                            <Button onClick={() => history.goBack()} isSecondary={true}>
                                Zurück
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </LoadingSpinner>
    );
};

export default SiloHistory;
